import {
  Box,
  Button,
  Divider,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { WbAvatar } from '@agilelab/plugin-wb-platform';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import CodeIcon from '@material-ui/icons/Code';
import EditIcon from '@material-ui/icons/Edit';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CancelIcon from '@material-ui/icons/Cancel';
import SettingsIcon from '@material-ui/icons/Settings';
import VisibilityIcon from '@material-ui/icons/Visibility';
import WarningIcon from '@material-ui/icons/Warning';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import RocketLaunchIcon from '@material-ui/icons/FlightTakeoff';
import PendingIcon from '@material-ui/icons/Schedule';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { useEntity } from '@mainstage/plugin-catalog-react';
import { configurationApiRef } from '@agilelab/plugin-wb-configuration-common';
import { useQuery } from '@tanstack/react-query';
import {
  type ComponentType,
  type CSSProperties,
  Fragment,
  useEffect,
  useMemo,
  useState,
} from 'react';

/** ─── Icon Map ─── */

const iconMap: Record<string, ComponentType<{ style?: CSSProperties }>> = {
  check: CheckIcon,
  close: CloseIcon,
  code: CodeIcon,
  edit: EditIcon,
  equalizer: EqualizerIcon,
  expand_more: ExpandMoreIcon,
  cancel: CancelIcon,
  settings: SettingsIcon,
  remove_red_eye: VisibilityIcon,
  warning: WarningIcon,
  savings: AccountBalanceWalletIcon,
  rocket_launch: RocketLaunchIcon,
  pending: PendingIcon,
};

function MIcon({ name, style }: { name: string; style?: CSSProperties }) {
  const IconComponent = iconMap[name];
  if (!IconComponent) return <span style={style}>{name}</span>;
  return <IconComponent style={style} />;
}

/** ─── Types ─── */

interface LifecycleStage {
  id: string;
  label: string;
  icon: string;
  status: 'completed' | 'next' | 'future' | 'pending';
}

interface Prerequisite {
  label: string;
  status: 'success' | 'error' | 'warning';
  icon: string;
}

interface NextStep {
  number: number;
  text: string;
}

interface CurrentStateCard {
  id: string;
  title: string;
  icon: string;
  status: 'approved' | 'pending' | 'rejected';
  description: string;
}

interface HistoryEvent {
  date: string;
  title: string;
  description: string;
  avatarUrl?: string;
  icon?: string | null;
  iconColor?: string;
}

interface HistoryGroup {
  group: string;
  events: HistoryEvent[];
}

interface LifecycleAction {
  id: string;
  label: string;
  icon: string;
  variant: 'primary' | 'secondary' | 'tertiary';
}

interface LifecycleData {
  currentState: string;
  stages: LifecycleStage[];
  prerequisites: Prerequisite[];
  nextSteps: NextStep[];
  currentStateCards: CurrentStateCard[];
  actions: LifecycleAction[];
  history: HistoryGroup[];
}

/** ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    height: '100%',
    backgroundColor: '#fafafa',
  },
  sidePanel: {
    width: 400,
    borderRight: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  sidePanelHeader: {
    height: 48,
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    borderBottom: '1px solid #eee',
  },
  sidePanelContent: {
    flex: 1,
    overflow: 'auto',
    padding: 12,
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    gap: 12,
  },
  stagesContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 12px',
  },
  stage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    width: 160,
  },
  stageIconFrame: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
  },
  stageCompleted: {
    border: '4px solid #8ac993',
  },
  stagePending: {
    border: '4px solid #ffb74d',
  },
  stageNext: {
    border: '1px dashed #cfd8dc',
  },
  stageFuture: {
    border: '1px dashed #cfd8dc',
  },
  stageLine: {
    flex: 1,
    height: 1,
    borderTop: '2px dashed #cfd8dc',
    minWidth: 40,
  },
  stageLineCompleted: {
    borderTop: '2px solid #8ac993',
  },
  cardsArea: {
    flex: 1,
    display: 'flex',
    gap: 12,
    padding: 12,
    overflow: 'auto',
  },
  currentStateSection: {
    flex: 1,
    border: '1px solid #eee',
    borderRadius: 6,
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  currentStateHeader: {
    height: 48,
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    borderBottom: '1px solid #eee',
  },
  currentStateContent: {
    flex: 1,
    position: 'relative',
    padding: 12,
    overflow: 'auto',
    backgroundColor: '#fafafa',
    backgroundImage: 'radial-gradient(circle, #e0e0e0 1px, transparent 1px)',
    backgroundSize: '30px 30px',
  },
  stepCard: {
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    padding: 12,
    backgroundColor: 'white',
    boxShadow: '0px 2px 4px rgba(146, 146, 146, 0.25)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 12,
  },
  historySection: {
    width: 400,
    border: '1px solid #eee',
    borderRadius: 6,
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  historyHeader: {
    height: 48,
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    borderBottom: '1px solid #eee',
  },
  historyContent: {
    flex: 1,
    overflow: 'auto',
    padding: 12,
  },
  historyEvent: {
    display: 'flex',
    gap: 16,
    marginBottom: 16,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 400,
  },
  badgeApproved: {
    backgroundColor: '#e6f3e8',
    border: '1px solid #00610e',
  },
  badgePending: {
    backgroundColor: '#fef6e9',
    border: '1px solid #c77c04',
  },
  badgeRejected: {
    backgroundColor: '#fde6e6',
    border: '1px solid #ec0000',
  },
  prerequisiteItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 8px',
    borderRadius: 6,
    marginTop: 4,
    marginLeft: 12,
  },
  prereqSuccess: {
    backgroundColor: '#e6f3e8',
  },
  prereqError: {
    backgroundColor: '#fde6e6',
  },
  prereqWarning: {
    backgroundColor: '#fef6e9',
  },
  nextStepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  nextStepNumber: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary?.main || '#006ca2',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 500,
    flexShrink: 0,
  },
  sectionLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '8px 4px 8px 8px',
    fontWeight: 700,
    fontSize: 12,
  },
  actionButton: {
    textTransform: 'none',
    borderRadius: 6,
    height: 32,
    fontSize: 12,
    fontWeight: 500,
    border: '1px solid #e0e0e0',
    color: theme.palette.primary?.main || '#006ca2',
  },
}));

/** ─── Sub-components ─── */

function StageIndicator({ stage }: { stage: LifecycleStage }) {
  const classes = useStyles();

  const frameClass = [
    classes.stageIconFrame,
    stage.status === 'completed' && classes.stageCompleted,
    stage.status === 'pending' && classes.stagePending,
    stage.status === 'next' && classes.stageNext,
    stage.status === 'future' && classes.stageFuture,
  ]
    .filter(Boolean)
    .join(' ');

  const iconColorMap: Record<string, string> = {
    completed: '#008914',
    pending: '#f57c00',
  };
  const iconColor = iconColorMap[stage.status] ?? '#aebac5';

  const labelColorMap: Record<string, string> = {
    completed: 'black',
    pending: '#f57c00',
  };
  const labelColor = labelColorMap[stage.status] ?? '#9e9e9e';

  return (
    <Box className={classes.stage}>
      <Typography
        style={{
          fontWeight: 700,
          fontSize: 14,
          color: labelColor,
          letterSpacing: 0.5,
        }}
      >
        {stage.label}
      </Typography>
      <Box className={frameClass}>
        <MIcon name={stage.icon} style={{ fontSize: 32, color: iconColor }} />
      </Box>
      {stage.status === 'next' && (
        <Typography style={{ fontSize: 12, fontWeight: 500 }}>
          Next State
        </Typography>
      )}
      {stage.status === 'pending' && (
        <Typography style={{ fontSize: 12, fontWeight: 500, color: '#f57c00' }}>
          Pending
        </Typography>
      )}
      {stage.status !== 'next' && stage.status !== 'pending' && (
        <Box style={{ height: 18 }} />
      )}
    </Box>
  );
}

function StageLine({ completed }: { completed?: boolean }) {
  const classes = useStyles();
  return (
    <Box
      className={`${classes.stageLine} ${
        completed ? classes.stageLineCompleted : ''
      }`}
    />
  );
}

function PrerequisiteItem({ item }: { item: Prerequisite }) {
  const classes = useStyles();
  const bgClassMap: Record<string, string> = {
    success: classes.prereqSuccess,
    error: classes.prereqError,
  };
  const bgClass = bgClassMap[item.status] ?? classes.prereqWarning;

  const iconColorMap: Record<string, string> = {
    success: '#008914',
    error: '#ec0000',
  };
  const iconColor = iconColorMap[item.status] ?? '#c77c04';

  return (
    <Box className={`${classes.prerequisiteItem} ${bgClass}`}>
      <MIcon name={item.icon} style={{ fontSize: 16, color: iconColor }} />
      <Typography style={{ fontSize: 12, fontWeight: 500 }}>
        {item.label}
      </Typography>
    </Box>
  );
}

function StatusBadge({ status }: { status: string }) {
  const classes = useStyles();
  const badgeClassMap: Record<string, string> = {
    approved: classes.badgeApproved,
    pending: classes.badgePending,
  };
  const badgeClass = badgeClassMap[status] ?? classes.badgeRejected;

  const statusIconMap: Record<string, string> = {
    approved: 'check',
    pending: 'pending',
  };
  const icon = statusIconMap[status] ?? 'close';
  const labelMap: Record<string, string> = {
    approved: 'Approved',
    pending: 'Pending',
  };
  const label = labelMap[status] ?? 'Rejected';

  return (
    <Box className={`${classes.badge} ${badgeClass}`}>
      <MIcon name={icon} style={{ fontSize: 16 }} />
      <span>{label}</span>
    </Box>
  );
}

function StepCard({ card }: { card: CurrentStateCard }) {
  const classes = useStyles();
  return (
    <Box className={classes.stepCard} style={{ width: 320 }}>
      <StatusBadge status={card.status} />
      <Box display="flex" alignItems="center" style={{ gap: 6 }}>
        <MIcon name={card.icon} style={{ fontSize: 16, color: '#4e6980' }} />
        <Typography style={{ fontWeight: 700, fontSize: 14 }}>
          {card.title}
        </Typography>
      </Box>
      <Typography style={{ fontSize: 12 }}>{card.description}</Typography>
    </Box>
  );
}

/** Arrow connector drawn with SVG */
function FlowArrow({
  direction,
}: {
  direction: 'down-left' | 'down-right' | 'down';
}) {
  const width = direction === 'down' ? 2 : 100;
  const height = 40;

  let path: string;
  if (direction === 'down-left') {
    path = `M ${width} 0 C ${width} ${height * 0.6}, 0 ${
      height * 0.4
    }, 0 ${height}`;
  } else if (direction === 'down-right') {
    path = `M 0 0 C 0 ${height * 0.6}, ${width} ${
      height * 0.4
    }, ${width} ${height}`;
  } else {
    path = `M 1 0 L 1 ${height}`;
  }

  return (
    <svg
      width={width || 2}
      height={height}
      style={{ display: 'block', margin: '0 auto' }}
      viewBox={`0 0 ${width || 2} ${height}`}
      fill="none"
    >
      <path
        d={path}
        stroke="#90a4ae"
        strokeWidth={1.5}
        strokeDasharray="4 3"
        fill="none"
      />
      {/* Arrowhead */}
      <polygon
        points={(() => {
          if (direction === 'down-left')
            return `0,${height} -4,${height - 6} 4,${height - 6}`;
          if (direction === 'down-right')
            return `${width},${height} ${width - 4},${height - 6} ${
              width + 4
            },${height - 6}`;
          return `1,${height} -3,${height - 6} 5,${height - 6}`;
        })()}
        fill="#90a4ae"
      />
    </svg>
  );
}

/** Flow layout for current state cards in a tree structure */
function CurrentStateFlow({ cards }: { cards: CurrentStateCard[] }) {
  if (cards.length === 0) return null;

  // Layout: first card on top, middle cards in a row, last card at bottom (if odd)
  const topCard = cards[0];
  const middleCards = cards.slice(
    1,
    cards.length % 2 === 0 ? cards.length : cards.length - 1,
  );
  const bottomCard =
    cards.length > 2 && cards.length % 2 !== 0 ? cards[cards.length - 1] : null;

  // For exactly 4 cards: top, 2 middle, 1 bottom (like the Figma)
  const row1 = [topCard];
  const row2 = cards.length >= 3 ? [cards[1], cards[2]] : middleCards;
  let row3: CurrentStateCard[] = [];
  if (cards.length >= 4) row3 = [cards[3]];
  else if (bottomCard) row3 = [bottomCard];

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      style={{ padding: '24px 12px', minHeight: '100%' }}
    >
      {/* Row 1 */}
      <Box display="flex" justifyContent="center">
        {row1.map(c => (
          <StepCard key={c.id} card={c} />
        ))}
      </Box>

      {/* Arrows from row 1 to row 2 */}
      {row2.length > 0 && (
        <Box display="flex" justifyContent="center" style={{ width: '100%' }}>
          {row2.length === 1 ? (
            <FlowArrow direction="down" />
          ) : (
            <Box display="flex" style={{ gap: 140 }}>
              <FlowArrow direction="down-left" />
              <FlowArrow direction="down-right" />
            </Box>
          )}
        </Box>
      )}

      {/* Row 2 */}
      {row2.length > 0 && (
        <Box display="flex" justifyContent="center" style={{ gap: 24 }}>
          {row2.map(c => (
            <StepCard key={c.id} card={c} />
          ))}
        </Box>
      )}

      {/* Arrows from row 2 to row 3 */}
      {row3.length > 0 && (
        <Box display="flex" justifyContent="center" style={{ width: '100%' }}>
          {row2.length >= 2 ? (
            <Box display="flex" style={{ gap: 140 }}>
              <FlowArrow direction="down-right" />
              <FlowArrow direction="down-left" />
            </Box>
          ) : (
            <FlowArrow direction="down" />
          )}
        </Box>
      )}

      {/* Row 3 */}
      {row3.length > 0 && (
        <Box display="flex" justifyContent="center">
          {row3.map(c => (
            <StepCard key={c.id} card={c} />
          ))}
        </Box>
      )}
    </Box>
  );
}

function HistoryEventItem({ event }: { event: HistoryEvent }) {
  const classes = useStyles();
  return (
    <Box className={classes.historyEvent}>
      <WbAvatar
        displayName={event.title}
        picture={event.avatarUrl || undefined}
        style={{ width: 48, height: 48 }}
      />
      <Box flex={1}>
        <Typography style={{ fontSize: 12, color: '#424242' }}>
          {event.date}
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          {event.icon && (
            <MIcon
              name={event.icon}
              style={{
                fontSize: 16,
                color:
                  (
                    { error: '#ec0000', primary: '#006ca2' } as Record<
                      string,
                      string
                    >
                  )[event.iconColor ?? ''] ?? '#4e6980',
              }}
            />
          )}
          <Typography style={{ fontWeight: 700, fontSize: 14 }}>
            {event.title}
          </Typography>
        </Box>
        <Typography style={{ fontSize: 12, color: '#424242' }}>
          {event.description}
        </Typography>
        <Typography
          component="a"
          style={{
            fontSize: 11,
            color: '#006ca2',
            textDecoration: 'underline',
            cursor: 'pointer',
            marginTop: 4,
            display: 'inline-block',
          }}
        >
          View more
        </Typography>
      </Box>
    </Box>
  );
}

/** ─── Main Component ─── */

export function LifecyclePage() {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configurationApiRef);
  const { entity } = useEntity();
  const dataProductName = entity.metadata.title || entity.metadata.name;

  // Load lifecycle data from the database (bypasses server-side LRU cache)
  const { data: fetchedData } = useQuery({
    queryKey: ['lifecycle-config'],
    queryFn: async () => {
      const result = await configApi.get<LifecycleData>({
        namespace: 'builder',
        key: 'lifecycle',
        noCache: true,
      });
      return result;
    },
    staleTime: 0,
  });

  const rawData: LifecycleData | undefined = fetchedData;

  // Replace {{dataProductName}} placeholders in history descriptions
  const baseData = useMemo<LifecycleData | undefined>(() => {
    if (!rawData) return undefined;
    const history = rawData.history.map(group => ({
      ...group,
      events: group.events.map(event => ({
        ...event,
        description: event.description.replace(
          /\{\{dataProductName\}\}/g,
          dataProductName,
        ),
      })),
    }));
    return { ...rawData, history };
  }, [rawData, dataProductName]);

  const [promotionRequested, setPromotionRequested] = useState(false);
  const [userName, setUserName] = useState('User');

  useEffect(() => {
    identityApi.getProfileInfo().then(profile => {
      if (profile.displayName) setUserName(profile.displayName);
    });
  }, [identityApi]);

  // Derive displayed data from state
  const data = useMemo<LifecycleData | undefined>(() => {
    if (!baseData) return undefined;
    if (!promotionRequested) return baseData;

    // Find the "next" stage and move it to "pending"
    const updatedStages = baseData.stages.map(stage => {
      if (stage.status === 'next')
        return { ...stage, status: 'pending' as const };
      return stage;
    });

    // Add a history entry at the top
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      '0',
    )}-${String(now.getDate()).padStart(2, '0')} ${String(
      now.getHours(),
    ).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const newEvent: HistoryEvent = {
      date: dateStr,
      title: 'Promotion to Operating requested',
      description: `${userName} requested promotion to the Operating stage.`,
      avatarUrl: '',
      icon: 'rocket_launch',
      iconColor: 'primary',
    };
    const updatedHistory: HistoryGroup[] = [
      {
        group: 'Just now',
        events: [newEvent],
      },
      ...baseData.history,
    ];

    return {
      ...baseData,
      stages: updatedStages,
      history: updatedHistory,
    };
  }, [promotionRequested, baseData, userName]);

  const classes = useStyles();

  if (!data) return null;

  const handleActionClick = (actionId: string) => {
    if (actionId === 'request-promotion') {
      setPromotionRequested(true);
    }
  };

  return (
    <Box className={classes.root}>
      {/* ─── Left Side Panel ─── */}
      <Box className={classes.sidePanel}>
        <Box className={classes.sidePanelHeader}>
          <Typography style={{ fontSize: 12, fontWeight: 500 }}>
            Next Steps
          </Typography>
        </Box>
        <Box className={classes.sidePanelContent}>
          {/* Prerequisites */}
          <Box className={classes.sectionLabel}>
            <MIcon
              name="expand_more"
              style={{ fontSize: 16, color: '#006ca2' }}
            />
            <Typography style={{ fontWeight: 700, fontSize: 12 }}>
              Prerequisites
            </Typography>
          </Box>
          {data.prerequisites.map((p, i) => (
            <PrerequisiteItem key={i} item={p} />
          ))}

          {/* What will happen when you promote */}
          <Box className={classes.sectionLabel} style={{ marginTop: 16 }}>
            <MIcon
              name="expand_more"
              style={{ fontSize: 16, color: '#006ca2' }}
            />
            <Typography style={{ fontWeight: 700, fontSize: 12 }}>
              What happens when require a promotion
            </Typography>
          </Box>
          <Paper
            elevation={0}
            style={{
              backgroundColor: '#e6f1f7',
              borderRadius: 6,
              padding: 12,
              marginLeft: 12,
            }}
          >
            {data.nextSteps.map(step => (
              <Box key={step.number} className={classes.nextStepItem}>
                <Box className={classes.nextStepNumber}>{step.number}</Box>
                <Typography style={{ fontSize: 12 }}>{step.text}</Typography>
              </Box>
            ))}
          </Paper>
        </Box>
      </Box>

      {/* ─── Main Area ─── */}
      <Box className={classes.mainContent}>
        {/* Toolbar with actions */}
        <Box className={classes.toolbar}>
          <Box flex={1} />
          {data.actions.map(action => (
            <Button
              key={action.id}
              className={classes.actionButton}
              startIcon={<MIcon name={action.icon} style={{ fontSize: 16 }} />}
              variant="outlined"
              disabled={promotionRequested && action.id === 'request-promotion'}
              onClick={() => handleActionClick(action.id)}
            >
              {action.label}
            </Button>
          ))}
        </Box>

        {/* Stage indicator row */}
        <Box className={classes.stagesContainer}>
          {data.stages.map((stage, idx) => (
            <Fragment key={stage.id}>
              <StageIndicator stage={stage} />
              {idx < data.stages.length - 1 && (
                <StageLine
                  completed={
                    (stage.status === 'completed' ||
                      stage.status === 'pending') &&
                    data.stages[idx + 1]?.status !== 'future'
                  }
                />
              )}
            </Fragment>
          ))}
        </Box>

        {/* Cards area */}
        <Box className={classes.cardsArea}>
          {/* Current state cards */}
          <Box className={classes.currentStateSection}>
            <Box className={classes.currentStateHeader}>
              <Typography style={{ fontSize: 12, fontWeight: 500 }}>
                Current state: what you can do now
              </Typography>
            </Box>
            <Box className={classes.currentStateContent}>
              <CurrentStateFlow cards={data.currentStateCards} />
            </Box>
          </Box>

          {/* History panel */}
          <Box className={classes.historySection}>
            <Box className={classes.historyHeader}>
              <Typography style={{ fontSize: 14, fontWeight: 500 }}>
                History
              </Typography>
            </Box>
            <Box className={classes.historyContent}>
              {data.history.map((group, gi) => (
                <Fragment key={group.group}>
                  {gi > 0 && <Divider style={{ margin: '12px 0' }} />}
                  <Typography
                    style={{
                      fontWeight: 700,
                      fontSize: 11,
                      color: '#2b3a46',
                      marginBottom: 12,
                    }}
                  >
                    {group.group}
                  </Typography>
                  {group.events.map((event, ei) => (
                    <HistoryEventItem key={ei} event={event} />
                  ))}
                </Fragment>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
